<script lang="ts">
  import { isAdmin, isSuperAdmin } from "../stores/store";
  import { deleteName, updateName, pseudoAnswer, isPseudoAnswer } from "../services/cleanup";

  export let data;
  export let projectId;
  export let removed = null;
  
  let error = false;
  let removeError = false;
  let replacement = "";
  let updateEnabled = true;
  let isUpdating = false;
  let isDeleting = false;
  let isPseudonymizing = false;
  let pseudoState = null; // null = unknown, true = pseudo, false = not pseudo

  console.log('named row data:', data);

  /**
   * Check if this is a pseudonymized name (has real name data available)
   */
  $: canGlobalUpdate = data.name && 
                      typeof data.name === 'string' && 
                      data.id && 
                      !matchesPseudonymPattern(data.name);

  /**
   * Display value - always show the name as-is
   */
  $: displayValue = data.name || '';

  /**
   * Check if value matches pseudonym pattern (Type ####)
   */
  function matchesPseudonymPattern(value) {
    if (!value || typeof value !== 'string') return false;
    const pseudonymPattern = /^[A-Za-z]+\s\d{4}$/;
    return pseudonymPattern.test(value.trim());
  }

  /**
   * Check pseudo state when component mounts
   */
  $: if (data && !canGlobalUpdate) {
    pseudoState = matchesPseudonymPattern(data.name);
  }

  /**
   * Global update - updates ALL instances of this name across the project
   */
  const globalUpdate = async () => {
    if (isUpdating || !canGlobalUpdate) return;
    
    error = false;
    if (!replacement || replacement.trim() === "") {
      error = true;
      return;
    }

    isUpdating = true;
    try {
      console.log("=== GLOBAL UPDATE (Named Entity) ===");
      console.log('Global update - updating name with ID:', data.id, 'to:', replacement.trim());
      
      const result = await updateName(projectId, data.id, replacement.trim());
      console.log('Global update result:', result);
      
      if (result && result.success) {
        console.log("✅ Global update successful");
        // Update local display
        data.name = replacement.trim();
        clearEditState();
        if (removed) {
          removed(); // Refresh the view
        }
      } else {
        console.error('❌ Global update failed:', result);
        error = true;
      }
    } catch (err) {
      console.error('❌ Error during global update:', err);
      error = true;
    } finally {
      isUpdating = false;
    }
  };

  /**
   * Pseudonymize this name - removes the real name data but keeps the pseudonym
   */
  const pseudonymize = async () => {
    if (isPseudonymizing || !confirm('Are you sure you want to pseudonymize this name? The real name will be hidden but the entity will remain visible.')) return;
    
    isPseudonymizing = true;
    removeError = false;
    
    try {
      console.log("=== PSEUDONYMIZE (Named Entity) ===");
      console.log("Pseudonymizing name with ID:", data.id);
      
      const result = await deleteName(projectId, data.id);
      
      if (result && result.success) {
        console.log("✅ Pseudonymize successful");
        pseudoState = true;
        clearEditState();
        if (removed) {
          removed(); // Refresh the view
        }
      } else {
        console.error('❌ Pseudonymize failed:', result);
        removeError = true;
      }
    } catch (err) {
      console.error('❌ Error during pseudonymize:', err);
      removeError = true;
    } finally {
      isPseudonymizing = false;
    }
  };

  /**
   * Remove this named entity completely (equivalent to deleteAnswer for named entities)
   */
  const removeEntity = async () => {
    if (isDeleting || !confirm('Are you sure you want to remove this entity? This will delete it from the system if no other users reference it.')) return;
    
    isDeleting = true;
    removeError = false;
    
    try {
      console.log("=== REMOVE ENTITY (Named Entity) ===");
      console.log("Removing entity with ID:", data.id);
      
      // Use deleteName which is equivalent to deleteAnswer for named entities
      // This will remove the entity completely if no other users reference it
      const result = await deleteName(projectId, data.id);
      
      if (result && result.success) {
        console.log("✅ Remove entity successful");
        clearEditState();
        if (removed) {
          removed(); // Refresh the view
        }
      } else {
        console.error('❌ Remove entity failed:', result);
        removeError = true;
      }
    } catch (err) {
      console.error('❌ Error during remove entity:', err);
      removeError = true;
    } finally {
      isDeleting = false;
    }
  };

  /**
   * Clear edit state
   */
  const clearEditState = () => {
    updateEnabled = true;
    replacement = "";
    removeError = false;
    error = false;
  };

  /**
   * Toggle edit mode
   */
  const toggleEdit = () => {
    updateEnabled = !updateEnabled;
    removeError = false;
    error = false;
    replacement = updateEnabled ? "" : displayValue; // Pre-populate with current value when editing
  };

  // Check if any operation is in progress
  $: isProcessing = isUpdating || isDeleting || isPseudonymizing;
</script>

<tr class="is-tight" class:highlight={!updateEnabled}>
  {#if $isAdmin}
    <td class="remove-padding">
      <button 
        class="btn" 
        class:btnhighlight={!updateEnabled} 
        disabled={isProcessing}
        on:click={toggleEdit}
      >
        {isProcessing ? 'Processing...' : (updateEnabled ? 'Edit' : 'Cancel')}
      </button>
    </td>
    
    <td>
      {displayValue}
      <!--{#if canGlobalUpdate}
        <span class="data-type-indicator">(has real name)</span>
      {:else if pseudoState === true}
        <span class="data-type-indicator">(pseudonymized)</span>
      {:else}
        <span class="data-type-indicator">(unknown state)</span>
      {/if}-->
    </td>
    
    <td class="no-border">
      <input 
        class:has-error={error} 
        bind:value={replacement} 
        type="text"
        disabled={updateEnabled || isProcessing}
        placeholder={displayValue}
      />
    </td>
    
    <td class="remove-padding is-narrow">
      <button 
        class="btn" 
        class:hidden={updateEnabled} 
        disabled={isProcessing || !canGlobalUpdate}
        on:click={globalUpdate}
      >
        {isUpdating ? 'Updating...' : 'Global Update'}
      </button>
    </td>
    
    <td class="is-danger">
      {#if canGlobalUpdate}
        <button 
          class="btn" 
          disabled={isProcessing}
          on:click={pseudonymize}
        >
          {isPseudonymizing ? 'Pseudonymizing...' : 'Pseudonymize'}
        </button>
      {:else}
        <span class="disabled-text">Already Pseudonymized</span>
      {/if}
    </td>
    
    <!---<td class="is-danger">
      <button 
        class="btn" 
        disabled={isProcessing}
        on:click={removeEntity}
      >
        {isDeleting ? 'Removing...' : 'Remove Entity'}
      </button>
    </td>-->
    
  {:else}
    <td>
      {displayValue}
      <!--{#if canGlobalUpdate}
        <span class="data-type-indicator">(has real name)</span>
      {:else if pseudoState === true}
        <span class="data-type-indicator">(pseudonymized)</span>
      {/if}-->
    </td>
  {/if}
</tr>

<!-- Error messages -->
{#if error}
  <tr>
    <td colspan="6" class="error-message">
      Error: Please enter a valid name
    </td>
  </tr>
{/if}

{#if removeError}
  <tr>
    <td colspan="6" class="error-message">
      Error: Could not remove this entity. It may still be referenced by other data.
    </td>
  </tr>
{/if}

<style lang="scss">
  button {
    line-height: 18px;
    font-weight: normal;
  }

  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  th.is-danger {
    width: 160px;
  }
  
  .is-danger button {
    width: 140px;
  }
  
  td.is-danger {
    width: 148px;
  }

  tr.is-tight td.is-danger {
    border-color: #fc72cd;
    border-bottom: none;
  }

  td.is-danger:not(:last-child) {
    border-right-width: 0;
    padding-right: 0;
    width: 144px;
  }
  
  td.is-danger:last-child {
    text-align: right;
  }

  .hidden {
    visibility: hidden;
  }

  .highlight {
    background-color: #e3e2d3;

    td {
      background-color: transparent;
    }

    .is-transparent {
      background-color: #d4d3bc !important;
    }
  }

  .btnhighlight {
    background-color: #fc72cd;
  }

  input.has-error {
    border: 1px solid red;
  }

  td.no-border {
    border-right: 0px;
  }

  .disabled-text {
    color: #999;
    font-style: italic;
    font-size: 0.9em;
  }

  .data-type-indicator {
    color: #666;
    font-size: 0.8em;
    font-style: italic;
    margin-left: 8px;
  }

  .error-message {
    color: red;
    font-style: italic;
    text-align: center;
    padding: 8px;
    background-color: #ffe6e6;
  }

  input:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
</style>